import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { PageSectionCard } from "../../../components/layout";
import {
  platformAdminService,
  type PlatformInvite,
} from "../../../services/platformAdminService";

const statusColor = (status: string): "success" | "warning" | "default" | "error" => {
  switch (status) {
    case "ACCEPTED":
      return "success";
    case "PENDING":
      return "warning";
    case "EXPIRED":
      return "error";
    default:
      return "default";
  }
};

const formatDate = (value?: string) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
};

export default function InvitesPanel() {
  const { t } = useTranslation();
  const [invites, setInvites] = useState<PlatformInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    platformAdminService
      .listInvites()
      .then((rows) => setInvites(Array.isArray(rows) ? rows : []))
      .catch((e: any) => setError(e?.message || t("superAdmin.invites.loadError")))
      .finally(() => setLoading(false));
  }, [t]);

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}
      <PageSectionCard
        title={t("superAdmin.invites.title")}
        description={t("superAdmin.invites.description")}
      >
        {loading ? (
          <CircularProgress size={28} />
        ) : invites.length === 0 ? (
          <Alert severity="info">{t("superAdmin.invites.empty")}</Alert>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("superAdmin.invites.email")}</TableCell>
                <TableCell>{t("superAdmin.invites.workspace")}</TableCell>
                <TableCell>{t("superAdmin.invites.role")}</TableCell>
                <TableCell>{t("superAdmin.invites.status")}</TableCell>
                <TableCell>{t("superAdmin.invites.invitedBy")}</TableCell>
                <TableCell>{t("superAdmin.invites.expires")}</TableCell>
                <TableCell>{t("superAdmin.invites.created")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {invites.map((invite) => (
                <TableRow key={invite.id} hover>
                  <TableCell>{invite.email}</TableCell>
                  <TableCell>{invite.workspaceName || invite.workspaceId}</TableCell>
                  <TableCell>{invite.role}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={t(`superAdmin.invites.statusValues.${invite.status}`, {
                        defaultValue: invite.status,
                      })}
                      color={statusColor(invite.status)}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>{invite.invitedBy || "—"}</TableCell>
                  <TableCell>{formatDate(invite.expiresAt)}</TableCell>
                  <TableCell>{formatDate(invite.createdDate)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </PageSectionCard>
    </Stack>
  );
}
